/** @jsxImportSource @emotion/react */
import React, { FC, PropsWithChildren, useCallback, useEffect, useState } from "react";
import { Text } from "../../components/general/Text";
import { css } from "@emotion/react";
import { FlexContainer } from "../../containers/FlexContainer";
import { GlobalStore } from "../../store/global/GlobalStore";
import { ReactComponent as BebopLogo } from "./icons/bebop.svg";
import bottomLightwaveImgSrc from "./icons/waitlist-bottom-lightwave.png";
import topLightwaveImgSrc from "./icons/waitlist-top-lightwave.svg";
import styled from "@emotion/styled";
import { Button } from "../../components/general/Button/Button";
import { addToWaitlist, addUserEmail, WaitlistStatus } from "./utils/allowListUtils";
import { useWaitlistStatus } from "./utils/useWaitlistStatus";
import { useUpdateAddressWithENS } from "../../hooks/useUpdateAddressWithENS";
import { MOBILE_WIDTH, TABLET_WIDTH } from "../../hooks/useDeviceType";
import { Link, useNavigate } from "react-router-dom";
import { useDisconnectInternal } from "hooks/useDisconnectInternal";
import { useAccount } from "wagmi";
import { NAV_LINKS } from "config/consts";

enum Errors {
  EmailHasBeenAdded,
  IssueAddingEmail,
}

export const HangInThere: FC = () => {
  useUpdateAddressWithENS();
  const address = GlobalStore.useState(s => s.address);
  const status = useWaitlistStatus();
  const ensName = GlobalStore.useState(s => s.ensName);
  const formattedAddress = address ? address.substring(0, 6) + "..." + address.slice(-4) : "";
  const [message, setMessage] = useState(`You're on the waitlist!`);
  const theme = GlobalStore.useState(s => s.theme);
  const [emailValue, setEmailValue] = useState("");
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [hasErrorAddingToList, setHasErrorAddingToList] = useState(false);
  const [updateError, setUpdateError] = useState<Errors | null>(null);
  const [hasSubmittedEmail, setHasSubmittedEmail] = useState(false);
  const [hasAttemptedToSubscribe, setHasAttemptedToSubscribe] = useState(false);
  const disconnect = useDisconnectInternal();
  const navigate = useNavigate();
  const { isConnecting, isReconnecting } = useAccount();

  useEffect(() => {
    if (address && status?.waitlistStatus === WaitlistStatus.ACCEPTED) {
      navigate("/trade");
    } else if (!address) {
      navigate("/signup");
    }
  }, [address, navigate, status]);

  if (
    address &&
    status?.waitlistStatus === WaitlistStatus.NOT_IN_WAITLIST &&
    !hasErrorAddingToList
  ) {
    addToWaitlist(address)
      .then(() => {
        setMessage(`You're on the waitlist`);
      })
      .catch(err => {
        console.error(err);
        setHasErrorAddingToList(true);
      });
  }
  const isValidEmail = (email: string): boolean => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const onClickSubscribe = useCallback(() => {
    if (!isEmailValid) {
      setHasAttemptedToSubscribe(true);
      return;
    }
    addUserEmail(address, emailValue)
      .then(async response => {
        if (response.status === 400) {
          if (((await response.json()) as { detail: string }).detail === "email already assigned") {
            setUpdateError(Errors.EmailHasBeenAdded);
          } else {
            setUpdateError(Errors.IssueAddingEmail);
          }
        } else {
          setHasSubmittedEmail(true);
        }
      })
      .catch(err => {
        console.error(err);
        setUpdateError(Errors.IssueAddingEmail);
      });
  }, [address, emailValue, isEmailValid]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const currentEmail = e.currentTarget.value;
    setUpdateError(null);
    setHasAttemptedToSubscribe(false);
    setEmailValue(currentEmail);
  };

  const showMailingListInput = !status?.hasEmail && !hasSubmittedEmail;
  const mailingListContents = showMailingListInput ? (
    <>
      <Text
        textStyle={"Title"}
        size={"Medium"}
        color={theme.font.secondary.default}
        shouldScale={false}
        textCss={css`
          text-align: left;
          margin-bottom: 15px;
        `}
      >
        Join our mailing list for occasional updates about Bebop
      </Text>
      <InputSection>
        <EmailInput
          placeholder={"Enter your email address"}
          type="email"
          value={emailValue}
          onChange={handleChange}
          onBlur={() => {
            setIsEmailValid(isValidEmail(emailValue));
          }}
        />
        <ButtonSubscribe buttonType={"primary"} text={"Subscribe"} onClick={onClickSubscribe} />
      </InputSection>
      {!isEmailValid && emailValue.length > 0 && hasAttemptedToSubscribe ? (
        <EmailValidationError />
      ) : null}
      {updateError === Errors.IssueAddingEmail ? <IssueAddingEmailText /> : null}
      {updateError === Errors.EmailHasBeenAdded ? <EmailHasBeenAddedText /> : null}
    </>
  ) : (
    <>
      {" "}
      <Text textStyle={"Header"} size={"Large"}>
        Thanks for subscribing
      </Text>
      <Text
        textStyle={"Title"}
        size={"Small"}
        textCss={css`
          margin-top: 15px;
          font-weight: 800;
        `}
        color={theme.font.secondary.default}
      >
        Your subscription has been confirmed.
      </Text>
    </>
  );

  if (isConnecting || isReconnecting) {
    return <></>;
  }

  return (
    <Container>
      <Contents>
        <TextContents>
          <Link to={NAV_LINKS.BEBOP}>
            <BebopLogo />
          </Link>
          <UserAddress>{ensName.length > 0 ? ensName : formattedAddress}</UserAddress>
          <WaitlistMessage>{message}</WaitlistMessage>
          <Description>
            Look out for a notification banner at bebop.xyz to find out when you get access.
          </Description>

          <ButtonsContainer>
            <ButtonCustom
              text={"Visit bebop.xyz"}
              onClick={() => {
                window.location.href = "https://bebop.xyz";
              }}
              buttonType={"secondary"}
            />
            <ButtonCustom text={"Disconnect wallet"} onClick={disconnect} buttonType={"tertiary"} />
          </ButtonsContainer>
        </TextContents>

        <MailingListContainer>
          <MailingList>{mailingListContents}</MailingList>
        </MailingListContainer>
      </Contents>

      {/* Lightwaves */}
      <BottomLightwaveBg src={bottomLightwaveImgSrc} alt="Bottom background lightwave" />
      <TopLightwaveBg src={topLightwaveImgSrc} alt="Top background lightwave" />
    </Container>
  );
};

// Sub components

const Container = styled.div`
  position: relative;
  width: 100dvw;
  height: 100%;
  min-height: 100dvh;
  background-color: black;
  overflow-x: hidden;
`;

const Contents = styled(FlexContainer)`
  flex-direction: column;
  padding-left: 18px;
  padding-top: clamp(38px, 4%, 53px);
  padding-right: 18px;
  align-items: start;
  margin-left: 8%;
  height: 100%;
  position: relative;
  z-index: 1;

  @media (max-width: ${MOBILE_WIDTH}px) {
    margin-left: 4%;
  }
`;

const TextContents = styled(FlexContainer)`
  flex-direction: column;
  align-items: flex-start;
  gap: 5px;
`;

const UserAddress = ({ children }: PropsWithChildren) => {
  return (
    <Text
      shouldScale={false}
      textStyle={"Header"}
      size={"Behemoth"}
      textCss={css`
        font-size: 10cqw;
        font-weight: 800;
        gap: 21px;
        line-height: 0.8;
        text-align: left;
        margin-top: 65px;
        line-height: 80%;
      `}
    >
      {children}
    </Text>
  );
};

const WaitlistMessage = ({ children }: PropsWithChildren) => {
  return (
    <Text
      shouldScale={false}
      textStyle={"Header"}
      size={"Behemoth"}
      textCss={css`
        font-size: 10cqw;
        font-weight: 800;
        line-height: 0.8;
        gap: 21px;
        text-align: center;
        line-height: 80%;
      `}
    >
      {children}
    </Text>
  );
};

const Description = ({ children }: PropsWithChildren) => {
  return (
    <Text
      textStyle={"Title"}
      size={"XL"}
      shouldScale={true}
      textCss={css`
        text-align: left;
        margin-top: min(32px, 5%);
        margin-bottom: min(32px, 5%);
        max-width: 555px;
      `}
    >
      {children}
    </Text>
  );
};

const ButtonsContainer = styled(FlexContainer)`
  gap: 16px;

  @media (max-width: ${MOBILE_WIDTH}px) {
    flex-direction: column;
    gap: 0;
  }
`;

const ButtonCustom = styled(Button)`
  width: 183px;
  height: 56px;
  justify-content: center;

  @media (max-width: ${MOBILE_WIDTH}px) {
    margin-top: 10px;
  }
`;

const MailingListContainer = styled.div`
  position: relative;
  left: 48%;
  top: min(200px, 4%);
  margin-bottom: 4%;

  @media (max-width: ${TABLET_WIDTH}px) {
    position: relative;
    bottom: unset;
    right: unset;
    left: unset;
    top: unset;
    margin-top: 30px;
  }
`;

const MailingList = styled.div`
  background: ${props => props.theme.surface.main.tertiary.default};
  border-radius: 24px;
  flex-direction: column;
  justify-content: flex-start;
  align-items: stretch;
  max-width: 500px;
  width: min(454px, 80dvw);
  padding: 24px;
  gap: 32px;
`;

const InputSection = styled(FlexContainer)`
  gap: 16px;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: flex-start;
`;

const EmailInput = styled.input`
  color: white;
  flex-grow: 1;
  max-width: 365px;
  background: ${props => props.theme.surface.main.primary.default};
  border: 0;
  height: 56px;
  box-sizing: border-box;
  border-radius: 14px;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  padding: 0 10px 0 12px;
  text-align: left;
  font-weight: 500;
  font-size: 16px;

  &:focus {
    outline: none;
  }

  ::placeholder {
    color: ${props => props.theme.font.quaternary.default};
  }
`;

const ButtonSubscribe = styled(Button)`
  padding: 19px 18px;
  border-radius: 14px;

  @media (max-width: ${MOBILE_WIDTH}px) {
    padding: 16px;
  }
`;

const IssueAddingEmailText = () => {
  const theme = GlobalStore.useState(s => s.theme);
  return (
    <Text
      textStyle={"Paragraph"}
      size={"Large"}
      textCss={css`
        text-align: left;
        margin-top: 16px;
        color: ${theme["Colour ramps"].Coral["500"]};
      `}
    >
      There was an issue adding your email to our list - please try again or contact support.{" "}
    </Text>
  );
};

const EmailHasBeenAddedText = () => {
  const theme = GlobalStore.useState(s => s.theme);
  return (
    <Text
      textStyle={"Paragraph"}
      size={"Large"}
      textCss={css`
        text-align: left;
        margin-top: 16px;
        color: ${theme["Colour ramps"].Coral["500"]};
      `}
    >
      Your email has already been added.
    </Text>
  );
};

const EmailValidationError = () => {
  const theme = GlobalStore.useState(s => s.theme);
  return (
    <Text
      textStyle={"Paragraph"}
      size={"Large"}
      textCss={css`
        margin-top: 14px;
        color: ${theme.font.alerts.error};
      `}
    >
      This email address doesn't look right. Check it and try again.{" "}
    </Text>
  );
};

const BottomLightwaveBg = styled.img`
  position: absolute;
  bottom: 0;
  left: 20dvw;
  width: 70%;

  @media (max-width: ${TABLET_WIDTH}px) {
    left: 13dvw;
    width: 80%;
  }

  @media (max-width: ${MOBILE_WIDTH}px) {
    left: 4dvw;
    width: 110%;
  }
`;

const TopLightwaveBg = styled.img`
  position: absolute;
  top: 0;
  right: 0;
  height: 40%;

  @media (max-width: ${TABLET_WIDTH}px) {
    height: 30%;
  }

  @media (max-width: ${MOBILE_WIDTH}px) {
    height: 28%;
  }
`;
